/**
 * SystemTab — Diagnóstico en tiempo real de todos los microservicios.
 * Solo visible para AdminGlobal. No usa react-query — fetch directo por tarjeta.
 */

import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './SystemTab.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CheckResult {
  status: 'ok' | 'error';
  durationMs?: number;
  error?: string;
  namespace?: string;
}

interface SubscriptionStat {
  topic: string;
  activeMessageCount: number;
  deadLetterMessageCount: number;
}

interface ServiceDiagnostics {
  service: string;
  version: string;
  environment: string;
  timestamp: string;
  status: 'healthy' | 'degraded' | 'unhealthy';
  checks: {
    mongodb?: CheckResult;
    redis?: CheckResult;
    serviceBus?: CheckResult;
    subscriptions?: Record<string, SubscriptionStat>;
  };
}

type CardState = 'idle' | 'loading' | 'ok' | 'error';

interface ServiceCard {
  id: string;
  label: string;
  url: string;
  icon: string;
  data: ServiceDiagnostics | null;
  state: CardState;
  errorMsg: string | null;
  lastChecked: Date | null;
}

// ─── Config ───────────────────────────────────────────────────────────────────

const SERVICES: Array<{ id: string; label: string; envKey: string; icon: string }> = [
  { id: 'survey',        label: 'SurveyService',       envKey: 'VITE_API_SURVEY_URL',        icon: 'fa-clipboard-check' },
  { id: 'analytics',     label: 'AnalyticsService',    envKey: 'VITE_API_ANALYTICS_URL',     icon: 'fa-chart-bar' },
  { id: 'meeting',       label: 'MeetingService',      envKey: 'VITE_API_MEETING_URL',       icon: 'fa-video' },
  { id: 'userAdmin',     label: 'UserAdminService',    envKey: 'VITE_API_USERS_URL',         icon: 'fa-users-cog' },
  { id: 'rules',         label: 'RulesEngine',         envKey: 'VITE_API_RULES_URL',         icon: 'fa-sliders-h' },
  { id: 'notifications', label: 'NotificationService', envKey: 'VITE_API_NOTIFICATIONS_URL', icon: 'fa-bell' },
];

const ENV_URLS: Record<string, string> = {
  VITE_API_SURVEY_URL:        import.meta.env.VITE_API_SURVEY_URL        ?? '',
  VITE_API_ANALYTICS_URL:     import.meta.env.VITE_API_ANALYTICS_URL     ?? '',
  VITE_API_MEETING_URL:       import.meta.env.VITE_API_MEETING_URL       ?? '',
  VITE_API_USERS_URL:         import.meta.env.VITE_API_USERS_URL         ?? '',
  VITE_API_RULES_URL:         import.meta.env.VITE_API_RULES_URL         ?? '',
  VITE_API_NOTIFICATIONS_URL: import.meta.env.VITE_API_NOTIFICATIONS_URL ?? '',
};

function buildCards(): ServiceCard[] {
  return SERVICES.map(s => ({
    id:          s.id,
    label:       s.label,
    url:         `${ENV_URLS[s.envKey]}/v1/diagnostics`,
    icon:        s.icon,
    data:        null,
    state:       'idle' as CardState,
    errorMsg:    null,
    lastChecked: null,
  }));
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({
  status,
  onClick,
  expanded,
}: {
  status: 'healthy' | 'degraded' | 'unhealthy' | 'loading' | 'idle' | 'error';
  onClick?: () => void;
  expanded?: boolean;
}) {
  const { t } = useTranslation();

  let cls = 'sys-badge';
  let icon = '';
  let label = '';

  if (status === 'loading')  { cls += ' sys-badge--loading'; icon = 'fa-spinner fa-spin'; label = t('system.checking'); }
  else if (status === 'idle')     { cls += ' sys-badge--idle';    icon = 'fa-circle';            label = '—'; }
  else if (status === 'healthy')  { cls += ' sys-badge--ok';      icon = 'fa-check-circle';      label = t('system.statusHealthy'); }
  else if (status === 'degraded') { cls += ' sys-badge--warn';    icon = 'fa-exclamation-triangle'; label = t('system.statusDegraded'); }
  else                            { cls += ' sys-badge--error';   icon = 'fa-times-circle';      label = t('system.statusError'); }

  if (onClick) {
    return (
      <button type="button" className={`${cls} sys-badge--clickable`} onClick={onClick}
        title={expanded ? t('system.hideDetails') : t('system.showDetails')}>
        <i className={`fas ${icon}`} aria-hidden="true" />
        {label}
        <i className={`fas fa-chevron-${expanded ? 'up' : 'down'} sys-badge-chevron`} aria-hidden="true" />
      </button>
    );
  }

  return <span className={cls}><i className={`fas ${icon}`} aria-hidden="true" /> {label}</span>;
}

function CheckRow({ name, check }: { name: string; check: CheckResult }) {
  const isOk = check.status === 'ok';
  return (
    <div className={`sys-check-row ${isOk ? 'sys-check--ok' : 'sys-check--error'}`}>
      <i className={`fas ${isOk ? 'fa-check-circle' : 'fa-times-circle'}`} aria-hidden="true" />
      <span className="sys-check-name">{name}</span>
      {isOk && check.durationMs != null && (
        <span className="sys-check-dur">{check.durationMs}ms</span>
      )}
      {check.namespace && <span className="sys-check-ns">{check.namespace}</span>}
      {!isOk && check.error && <span className="sys-check-error">{check.error}</span>}
    </div>
  );
}

function SubscriptionRows({ subs }: { subs: Record<string, SubscriptionStat> }) {
  const { t } = useTranslation();
  return (
    <div className="sys-sub-list">
      <div className="sys-sub-header">{t('system.checkSubscriptions')}</div>
      {Object.entries(subs).map(([name, stat]) => (
        <div key={name} className="sys-sub-row">
          <span className="sys-sub-name">{name}</span>
          <span className="sys-sub-topic">{stat.topic}</span>
          <span className={`sys-sub-count ${stat.activeMessageCount > 0 ? 'sys-sub-count--active' : ''}`}>
            <i className="fas fa-envelope" aria-hidden="true" /> {stat.activeMessageCount}
          </span>
          {stat.deadLetterMessageCount > 0 && (
            <span className="sys-sub-dlq">
              <i className="fas fa-skull-crossbones" aria-hidden="true" /> {stat.deadLetterMessageCount}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

const ServiceBusSubscriptions = {
  AnalyticsWorkerResponses: 'analytics-worker-responses',
  ExpirationWorkerSurveys:  'expiration-worker-surveys',
} as const;

function WorkersCard({ surveyData }: { surveyData: ServiceDiagnostics | null }) {
  const { t } = useTranslation();
  const subs = surveyData?.checks?.subscriptions;

  const analyticsWorkerSub = subs?.[ServiceBusSubscriptions.AnalyticsWorkerResponses];
  const expirationWorkerSub = subs?.[ServiceBusSubscriptions.ExpirationWorkerSurveys];

  const hasSubs = analyticsWorkerSub ?? expirationWorkerSub;

  return (
    <div className="sys-card sys-card--workers">
      <div className="sys-card-header">
        <div className="sys-card-icon"><i className="fas fa-cogs" aria-hidden="true" /></div>
        <div className="sys-card-info">
          <div className="sys-card-name">{t('system.workers')}</div>
          <div className="sys-card-sub">{t('system.workersDesc')}</div>
        </div>
        <div className="sys-card-status" />
      </div>

      {hasSubs ? (
        <div className="sys-card-body">
          {analyticsWorkerSub && (
            <div className="sys-worker-row">
              <i className="fas fa-chart-line sys-worker-icon" aria-hidden="true" />
              <span className="sys-worker-name">{t('system.analyticsWorker')}</span>
              <WorkerStat sub={analyticsWorkerSub} />
            </div>
          )}
          {expirationWorkerSub && (
            <div className="sys-worker-row">
              <i className="fas fa-hourglass-half sys-worker-icon" aria-hidden="true" />
              <span className="sys-worker-name">{t('system.expirationWorker')}</span>
              <WorkerStat sub={expirationWorkerSub} />
            </div>
          )}
        </div>
      ) : (
        <div className="sys-card-body sys-no-stats">{t('system.noStats')}</div>
      )}
    </div>
  );
}

function WorkerStat({ sub }: { sub: SubscriptionStat }) {
  const { t } = useTranslation();
  return (
    <div className="sys-worker-stats">
      <span className={`sys-sub-count ${sub.activeMessageCount > 0 ? 'sys-sub-count--active' : ''}`}>
        <i className="fas fa-envelope" aria-hidden="true" /> {sub.activeMessageCount}
      </span>
      {sub.deadLetterMessageCount > 0 && (
        <span className="sys-sub-dlq">
          <i className="fas fa-skull-crossbones" aria-hidden="true" /> {sub.deadLetterMessageCount} DLQ
        </span>
      )}
      <span className="sys-worker-topic">{sub.topic}</span>
      {sub.activeMessageCount === 0 && sub.deadLetterMessageCount === 0 && (
        <span className="sys-worker-ok"><i className="fas fa-check-circle" aria-hidden="true" /> {t('system.ok')}</span>
      )}
    </div>
  );
}

// ─── Service card ─────────────────────────────────────────────────────────────

function ServiceDiagCard({
  card,
  onRefresh,
}: {
  card: ServiceCard;
  onRefresh: (id: string) => void;
}) {
  const { t } = useTranslation();
  const { data, state, errorMsg, lastChecked } = card;

  const statusForBadge = state === 'loading' ? 'loading'
    : state === 'idle'    ? 'idle'
    : state === 'error'   ? 'error'
    : (data?.status ?? 'error');

  // Auto-expand when degraded or error; collapsed by default when healthy
  const isDegraded = statusForBadge === 'degraded' || statusForBadge === 'error' || statusForBadge === 'unhealthy';
  const [expanded, setExpanded] = useState(isDegraded);

  // Re-sync when status changes (e.g. after a refresh)
  useEffect(() => {
    setExpanded(isDegraded);
  }, [isDegraded]);

  const hasDetails = !!data;

  return (
    <div className={`sys-card sys-card--${statusForBadge}`}>
      <div className="sys-card-header">
        <div className="sys-card-icon"><i className={`fas ${card.icon}`} aria-hidden="true" /></div>
        <div className="sys-card-info">
          <div className="sys-card-name">{card.label}</div>
          {data && (
            <div className="sys-card-meta">
              <span className="sys-card-env">{data.environment}</span>
              <span className="sys-card-version">v{data.version}</span>
            </div>
          )}
        </div>
        <div className="sys-card-status">
          <StatusBadge
            status={statusForBadge}
            onClick={hasDetails ? () => setExpanded(e => !e) : undefined}
            expanded={expanded}
          />
        </div>
        <button
          className="sys-refresh-btn"
          onClick={() => onRefresh(card.id)}
          disabled={state === 'loading'}
          title={t('system.refresh')}
          type="button"
        >
          <i className={`fas fa-sync-alt${state === 'loading' ? ' fa-spin' : ''}`} aria-hidden="true" />
        </button>
      </div>

      {lastChecked && (
        <div className="sys-last-checked">
          {t('system.lastChecked')}: {lastChecked.toLocaleTimeString()}
        </div>
      )}

      {state === 'error' && errorMsg && (
        <div className="sys-card-error">{errorMsg}</div>
      )}

      {data && expanded && (
        <div className="sys-card-body">
          {data.checks.mongodb    && <CheckRow name={t('system.checkMongodb')}    check={data.checks.mongodb} />}
          {data.checks.redis      && <CheckRow name={t('system.checkRedis')}      check={data.checks.redis} />}
          {data.checks.serviceBus && <CheckRow name={t('system.checkServiceBus')} check={data.checks.serviceBus} />}
          {data.checks.subscriptions && Object.keys(data.checks.subscriptions).length > 0 && (
            <SubscriptionRows subs={data.checks.subscriptions} />
          )}
        </div>
      )}
    </div>
  );
}

// Stable URL map (never changes after module load)
const CARD_URLS: Record<string, string> = Object.fromEntries(
  SERVICES.map(s => [s.id, `${ENV_URLS[s.envKey]}/v1/diagnostics`])
);

// ─── Main component ───────────────────────────────────────────────────────────

export function SystemTab() {
  const { t } = useTranslation();
  const [cards, setCards] = useState<ServiceCard[]>(() => buildCards());
  const [refreshingAll, setRefreshingAll] = useState(false);

  const fetchCard = useCallback(async (id: string) => {
    setCards(prev => prev.map(c => c.id === id ? { ...c, state: 'loading', errorMsg: null } : c));

    const url = CARD_URLS[id];
    if (!url) return;

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8_000);
      let res: Response;
      try {
        res = await fetch(url, { signal: controller.signal });
      } finally {
        clearTimeout(timeout);
      }

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP ${res.status}: ${text.slice(0, 200)}`);
      }

      const data = await res.json() as ServiceDiagnostics;
      setCards(prev => prev.map(c =>
        c.id === id ? { ...c, state: 'ok', data, errorMsg: null, lastChecked: new Date() } : c
      ));
    } catch (err) {
      const msg = err instanceof Error
        ? (err.name === 'AbortError' ? 'Timeout (8s) — el servicio no respondió.' : err.message)
        : 'Error desconocido';
      setCards(prev => prev.map(c =>
        c.id === id ? { ...c, state: 'error', data: null, errorMsg: msg, lastChecked: new Date() } : c
      ));
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setRefreshingAll(true);
    await Promise.all(SERVICES.map(s => fetchCard(s.id)));
    setRefreshingAll(false);
  }, [fetchCard]);

  // Auto-fetch on mount
  useEffect(() => {
    void fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const surveyCard = cards.find(c => c.id === 'survey');

  return (
    <div className="sys-tab">
      {/* Header */}
      <div className="sys-header">
        <div>
          <h2 className="sys-title">
            <i className="fas fa-heartbeat" aria-hidden="true" /> {t('system.title')}
          </h2>
          <p className="sys-subtitle">{t('system.subtitle')}</p>
          <span className="sys-frontend-version">
            <i className="fas fa-code-branch" aria-hidden="true" /> Frontend&nbsp;
            <code>{__APP_VERSION__}</code>
          </span>
        </div>
        <button
          className="sys-btn-refresh-all"
          onClick={() => void fetchAll()}
          disabled={refreshingAll}
          type="button"
        >
          <i className={`fas fa-sync-alt${refreshingAll ? ' fa-spin' : ''}`} aria-hidden="true" />
          {t('system.refreshAll')}
        </button>
      </div>

      {/* Service cards grid */}
      <div className="sys-grid">
        {cards.map(card => (
          <ServiceDiagCard key={card.id} card={card} onRefresh={id => void fetchCard(id)} />
        ))}

        {/* Workers pseudo-card */}
        <WorkersCard surveyData={surveyCard?.data ?? null} />
      </div>

      {/* Legend */}
      <div className="sys-legend">
        <div className="sys-legend-title">
          <i className="fas fa-info-circle" aria-hidden="true" />
          {t('system.legendTitle')}
        </div>
        <div className="sys-legend-items">
          <div className="sys-legend-item">
            <span className="sys-badge sys-badge--ok"><i className="fas fa-check-circle" aria-hidden="true" /> {t('system.statusHealthy')}</span>
            <span className="sys-legend-desc">{t('system.legendHealthyDesc')}</span>
          </div>
          <div className="sys-legend-item">
            <span className="sys-badge sys-badge--warn"><i className="fas fa-exclamation-triangle" aria-hidden="true" /> {t('system.statusDegraded')}</span>
            <span className="sys-legend-desc">{t('system.legendDegradedDesc')}</span>
          </div>
          <div className="sys-legend-item">
            <span className="sys-badge sys-badge--error"><i className="fas fa-times-circle" aria-hidden="true" /> {t('system.statusError')}</span>
            <span className="sys-legend-desc">{t('system.legendErrorDesc')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
